"use client";

import React from "react";

import { SliderRoot, SliderThumb } from "@/components/ui/slider";

interface UseLogSliderResult {
  value: number;
  getPercentPosition: (value: number) => number;
  getSliderRootProps: () => React.ComponentPropsWithoutRef<typeof SliderRoot>;
  getSliderThumbProps: () => React.ComponentPropsWithoutRef<typeof SliderThumb>;
}

interface UseLogSliderOptions {
  initialValue: number;
  minValue: number;
  maxValue: number;
  minPosition?: number;
  maxPosition?: number;
}

function roundToSliderWholeNumber(value: number) {
  const placesAboveDecimal = Math.floor(Math.log10(value));
  const roundVariable =
    10 **
    (placesAboveDecimal <= 3 ? placesAboveDecimal : placesAboveDecimal - 1);

  return Math.round(value / roundVariable) * roundVariable;
}

export function useLogSlider({
  initialValue = 10_000,
  minValue,
  maxValue,
  minPosition = 0,
  maxPosition = 700,
}: UseLogSliderOptions): UseLogSliderResult {
  const minLogValue = Math.log(minValue || 1);
  const maxLogValue = Math.log(maxValue);

  function getScale() {
    return (maxLogValue - minLogValue) / (maxPosition - minPosition);
  }

  function getValue(position: number) {
    return Math.exp(minLogValue + getScale() * (position - minPosition));
  }

  function getPosition(value: number) {
    return minPosition + (Math.log(value) - minLogValue) / getScale();
  }

  const [currentPosition, setCurrentPosition] = React.useState(
    Math.round(getPosition(initialValue))
  );

  return {
    value: roundToSliderWholeNumber(getValue(currentPosition)),
    getPercentPosition: (value: number) =>
      (getPosition(value) - minPosition) / (maxPosition - minPosition),
    getSliderRootProps: () => ({
      defaultValue: [currentPosition],
      min: minPosition,
      max: maxPosition,
      value: [currentPosition],
      onValueChange: (value) => setCurrentPosition(value[0] ?? 0),
    }),
    getSliderThumbProps: () => ({
      "aria-valuemin": minValue,
      "aria-valuemax": maxValue,
      "aria-valuenow": roundToSliderWholeNumber(getValue(currentPosition)),
    }),
  };
}
